<template>
  <main class="l-crew-settings-wrapper">
    <div class="grid-container full">
      <div class="grid-margin-x">
        <div class="cell u-no-margin">
          <div class="action action--page u-extra-small-margin--bottom">
            <router-link
              :to="{ name : 'weeks', params : { date: startdate } }"
              class="button button--prev"
              :title="$t('prev_page')"
            >{{ $t('prev_page') }}</router-link>
            <h1 class="u-no-margin">{{ getPageTitle}} <span class="u-color--primary">KW {{ week }}</span></h1>
            <div v-show="isLoading">
              <v-page-loading />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="grid-container full">
      <div class="grid-x grid-margin-x">
        <div class="cell medium-9 medium-offset-1 large-11 large-offset-0 u-no-margin">

          <!-- Right side aboslute positioned buttons -->
          <v-action-buttons :items="getActionButtons" @actionClicked="onClickAction" />

          <KolonneSettingsEmployeesModal
            v-if="shouldShowEmployeesModal"
            :isVisible="shouldShowEmployeesModal"
            :week="week"
            :startdate="startdate"
            :selected-kolonne-id="selectedKolonneId"
            :selected-kolonne-number="selectedKolonneNumber"
            :selected-employee-ids="selectedEmployeeIds"
            :available-employees="allEmployees"
            :kolonne-content="kolonne_content"
            v-model="shouldShowEmployeesModal"
            @saved="onEmployeesSavedFromModal"
            @refreshedEmployees="onEmployeesRefreshed"
          />

          <KolonneSettingsVehiclesModal
            v-if="shouldShowVehiclesModal"
            :isVisible="shouldShowVehiclesModal"
            :week="week"
            :startdate="startdate"
            :selected-kolonne-id="selectedKolonneId"
            :selected-kolonne-number="selectedKolonneNumber"
            :selected-vehicle-ids="selectedVehicleIds"
            :available-vehicles="allVehicles"
            :kolonne-content="kolonne_content"
            v-model="shouldShowVehiclesModal"
            @saved="onVehiclesSavedFromModal"
            @refreshedVehicles="onVehiclesRefreshed"
          />

          <!-- Old, deprecated Vehicle Settings Modal: kept to keep a working version for legacy data -->
          <v-vehicles-modal
            :isVisible="vehiclesModal.isVisible"
            v-model="vehiclesModal.isVisible"
            :current_kolonne_id="vehiclesModal.current_kolonne_id"
            :current_vehicles="vehiclesModal.current_vehicles"
            :current_kolonne_number="vehiclesModal.current_kolonne_number"
            @vehicles="refreshVehiclesFromModal"
          ></v-vehicles-modal>

          <!-- Dupication Modal starts here-->
          <div class="dialog" :class="{ 'active' : showDuplicateDialog }">
            <div class="dialog--content dialog--medium-width">
              <h3>{{ $t('kolonne.settings.duplicate.title') }}</h3>

              <p class="u-extra-medium-margin--bottom">
                Personen:
                <i v-if="duplicateItem.crew">{{ duplicateItem.crew }}</i>
                <i v-else-if="duplicateItem.employees && duplicateItem.employees.length > 0">{{ duplicateItem.employees.map(employee => `${employee.firstName} ${employee.lastName}`) }}</i>
                <i v-else>keine gewählt</i>,

                {{ $t('form.project.site_contact_info') }}:
                <i>{{ duplicateItem.contact }}</i>
              </p>

              <p class="u-extra-medium-margin--bottom">
                {{ $t('kolonne.settings.duplicate.desc') }}
              </p>

              <div class="grid-x grid-margin-x">
                <div class="cell small-auto medium-6">
                  <div class="form-group">
                    <label>{{ $t("kolonne.number") }}</label>
                    <v-select-kolonne
                      id="form-field-kolonne"
                      name="kolonne"
                      :placeholder="$t('select_all')"
                      v-model="duplicateItem.target_kolonne"
                    />
                  </div>
                </div>
                <div class="cell small-auto medium-6">
                  <div class="form-group">
                    <label>Datum</label>
                    <v-datepicker
                      id="pickdate"
                      class="form-control form-control--small form-control--date"
                      :placeholder="'Datum'"
                      :format="$helpers.dateFormat"
                      :clearButton="true"
                      :monday-first="true"
                      :language="de"
                      :disabled-dates="{ days: [6, 0], to : new Date(duplicateItem.next_week_startdate) }"
                      v-model="duplicateItem.target_date"
                    />
                  </div>
                </div>
              </div>

              <p class="text-center">
                <button
                  class="button"
                  @click="duplicateKolonneSettings()"
                  :disabled="isLoading"
                >{{ $t('save') }}</button>
                <button class="button gray" @click="onCloseDuplicateDialog()">{{ $t('cancel') }}</button>
              </p>

              <button class="close-button" @click.prevent="onCloseDuplicateDialog()">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <!-- Dupication Modal ends here-->

          <div class="box u-extra-large-padding--bottom u-extra-medium-padding--top">
            <!-- Header information in grid form -->
            <div class="grid-x grid-margin-x">
              <div class="cell small-1"></div>

              <div class="cell auto">
                <div class="grid-x grid-margin-x">
                  <div class="cell medium-4">
                    <p class="u-extra-small-margin--bottom">
                      <strong>{{ $t('personnel') }}</strong>
                    </p>
                  </div>
                  <div class="cell medium-4">
                    <p class="u-extra-small-margin--bottom">
                      <strong>{{ $t('kolonne.settings.vehicles') }}</strong>
                    </p>
                  </div>
                  <div class="cell medium-3">
                    <p class="u-extra-small-margin--bottom">
                      <strong>{{ $t('kolonne.settings.comment') }}</strong>
                    </p>
                  </div>
                  <div class="cell small-1">
                    <p class="u-extra-small-margin--bottom text-center">
                      <strong>{{ $t('duplicate') }}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Content starts here -->
            <section
              v-for="(kolonneSettings, index) in this.kolonne_content"
              :key="index"
              class="u-extra-small-margin--bottom"
            >
              <div class="grid-x grid-margin-x align-middle">
                <div class="cell small-1">
                  <label class="u-no-margin--bottom">
                    <input type="checkbox" name="selected_crews[]" v-model="kolonneSettings.active" />
                    <i></i>
                    <span class="h5">{{ kolonneSettings.kolonne }}</span>
                  </label>
                </div>
                <div class="cell auto">
                  <div class="grid-x grid-margin-x">
                    <div class="cell medium-4 u-background u-background--gray">
                      <p v-if="!kolonneSettings.active && !kolonneSettings.id" class="u-text-center u-extra-small-margin--bottom u-extra-small-margin--top">
                        {{ $t('kolonne.settings.notactivated') }}
                      </p>

                      <p v-else-if="kolonneSettings.active && !kolonneSettings.id" class="u-text-center u-extra-small-margin--bottom u-extra-small-margin--top">
                        {{ $t('kolonne.settings.notsaved') }}
                      </p>

                      <textarea v-else-if="kolonneSettings.crew" v-model="kolonneSettings.crew" class="form__textarea--small"></textarea>

                      <div v-else class="c-crew-settings-box u-no-margin">
                        <template v-if="Array.isArray(kolonneSettings.employees_ids) && kolonneSettings.employees_ids.length > 0">
                          <ul>
                            <li v-for="employee in kolonneSettings.employees" :key="employee.id">
                              <KolonneSettingsAssignedEmployee
                                :employee="employee"
                                :startdate="startdate"
                                :kolonne-content="kolonne_content"
                                :selected-kolonne-id="kolonneSettings.id"
                              />
                            </li>
                          </ul>

                          <button
                            :title="$t('update')"
                            class="o-crew-button o-crew-button--edit"
                            @click.prevent="openEmployeesModal(kolonneSettings.id, kolonneSettings.kolonne)"
                            >
                            <svg class="u-icon--edit" role="presentation">
                              <use
                              xlink:href="/dist/sprite.svg#icon-edit"
                              href="/dist/sprite.svg#icon-edit"
                              />
                            </svg>
                          </button>
                        </template>

                        <template v-else>
                          <button
                            :title="$t('create_new')"
                            class="o-crew-button o-crew-button--add--hover"
                            @click.prevent="openEmployeesModal(kolonneSettings.id, kolonneSettings.kolonne)"
                          >
                            <svg class="u-icon--add" role="presentation">
                              <use
                              xlink:href="/dist/sprite.svg#icon-add"
                              href="/dist/sprite.svg#icon-add"
                              />
                            </svg>
                          </button>
                        </template>
                      </div>
                    </div>

                    <div class="cell medium-4 u-background u-background--gray">
                      <p v-if="!kolonneSettings.active && !kolonneSettings.id" class="u-text-center u-extra-small-margin--bottom u-extra-small-margin--top">
                        {{ $t('kolonne.settings.notactivated') }}
                      </p>

                      <p v-else-if="kolonneSettings.active && !kolonneSettings.id" class="u-text-center u-extra-small-margin--bottom u-extra-small-margin--top">
                        {{ $t('kolonne.settings.notsaved') }}
                      </p>

                      <div v-else class="c-crew-settings-box u-no-margin">
                        <template v-if="Array.isArray(kolonneSettings.vehicles) && kolonneSettings.vehicles.length >= 1">
                          <ul class="c-vehicle__list">
                              <li v-for="(kolonneSettings, index) in kolonneSettings.vehicles" :key="index">
                                <strong>{{kolonneSettings.type}}:</strong> {{kolonneSettings.number}}
                              </li>
                          </ul>

                          <button :title="$t('update')" class="o-crew-button o-crew-button--edit" @click.prevent="openDeprecatedVehiclesModal(kolonneSettings.id, kolonneSettings.vehicles, kolonneSettings.kolonne)">
                            <svg class="u-icon--edit" role="presentation">
                              <use
                              xlink:href="/dist/sprite.svg#icon-edit"
                              href="/dist/sprite.svg#icon-edit"
                              />
                            </svg>
                          </button>
                        </template>

                        <template v-else-if="Array.isArray(kolonneSettings.fleet) && kolonneSettings.fleet.length > 0">
                          <ul>
                            <li v-for="vehicle in kolonneSettings.fleet" :key="vehicle.id">
                              <KolonneSettingsAssignedVehicle
                                :vehicle="vehicle"
                                :startdate="startdate"
                                :kolonne-content="kolonne_content"
                                :selected-kolonne-id="kolonneSettings.id"
                              />
                              <!-- {{ vehicle.name }} <span v-if="vehicle.categoryName">({{ vehicle.categoryName }})</span> -->
                            </li>
                          </ul>

                          <button
                            :title="$t('update')"
                            class="o-crew-button o-crew-button--edit"
                            @click.prevent="openVehiclesModal(kolonneSettings.id, kolonneSettings.kolonne)"
                            >
                            <svg class="u-icon--edit" role="presentation">
                              <use
                              xlink:href="/dist/sprite.svg#icon-edit"
                              href="/dist/sprite.svg#icon-edit"
                              />
                            </svg>
                          </button>
                        </template>

                        <template v-else>
                          <button
                            :title="$t('create_new')"
                            class="o-crew-button o-crew-button--add--hover"
                            @click.prevent="openVehiclesModal(kolonneSettings.id, kolonneSettings.kolonne)"
                          >
                            <svg class="u-icon--add" role="presentation">
                              <use
                              xlink:href="/dist/sprite.svg#icon-add"
                              href="/dist/sprite.svg#icon-add"
                              />
                            </svg>
                          </button>
                        </template>
                      </div>
                    </div>

                    <div class="cell medium-3">
                      <p v-if="!kolonneSettings.active && !kolonneSettings.id" class="u-text-center u-extra-small-margin--top u-extra-small-margin--bottom">
                        {{ $t('kolonne.settings.notactivated') }}
                      </p>

                      <AutosizeTextarea
                        v-else
                        v-model="kolonneSettings.comment"
                        rows="1"
                        class="form__textarea--small"
                      />
                    </div>

                    <div class="cell small-1 flex-container flex-dir-column align-middle">
                      <a
                        :title="$t('duplicate')"
                        @click.prevent="onOpenDuplicateDialog(kolonneSettings)"
                        class="button button--action button--action--duplicate button--small button--medium"
                      >
                        {{ $t('duplicate') }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div class="grid-x grid-margin-x align-middle">
              <div class="cell small-1"></div>
              <div class="cell auto">
                <div class="grid-x grid-margin-x align-middle">
                  <div class="cell auto">
                    <hr>
                    <KolonneSettingsCrudEmployeesRankingLegend />
                  </div>
                  <div class="cell auto">
                    <hr>
                    <KolonneSettingsCrudVehiclesRankingLegend />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- .box-->
        </div>
        <!-- .cell-->
      </div>
      <!-- .grid-x-->
    </div>
    <!-- .grid-container -->
  </main>
</template>
<script>
import Datepicker from "vuejs-datepicker";
import { de } from "vuejs-datepicker/dist/locale";
import PageLoading from "./../partials/PageLoading";
import VehiclesModal from "./../partials/KolonneVehiclesModal";
import SelectKolonne from "./../partials/Forms/SelectKolonne";
import ActionButtons from "./../partials/Foundation/ActionButtons";
import AutosizeTextarea from "./../partials/Forms/AutosizeTextarea.vue";
import KolonneSettingsVehiclesModal from "./partials/KolonneSettingsVehicles/KolonneSettingsVehiclesModal.vue";
import KolonneSettingsEmployeesModal from "./partials/KolonneSettingsEmployees/KolonneSettingsEmployeesModal.vue";
import KolonneSettingsAssignedVehicle from "./partials/KolonneSettingsVehicles/KolonneSettingsAssignedVehicle.vue";
import KolonneSettingsCrudVehiclesRankingLegend from "./partials/KolonneSettingsVehicles/KolonneSettingsCrudVehiclesRankingLegend.vue";
import KolonneSettingsAssignedEmployee from "./partials/KolonneSettingsEmployees/KolonneSettingsAssignedEmployee.vue";
import KolonneSettingsCrudEmployeesRankingLegend from "./partials/KolonneSettingsEmployees/KolonneSettingsCrudEmployeesRankingLegend.vue";

export default {
  name: "KolonneSettings",
  data: function() {
    return {
      isLoading: false,
      duplicateAll: false,
      allEmployees: [],
      allVehicles: [],
      kolonne: [],
      kolonne_settings: [],
      kolonne_content: [],
      startdate: "",
      week: "",
      showDuplicateDialog: false,
      duplicateItem: {
        id: 0,
        kolonne: "",
        vehicles: [],
        fleet_ids: [],
        crew: "",
        employees_ids: [],
        contact: "",
        target_kolonne: "",
        target_date: ""
      },
      /**
       * This "vehiclesModal" property is still used by the deprected vehicles modal.
       * We added a newer version of a vehicles modal using data from geoCapture
       * in August 2024. That new modal uses the "vehicles" variables below.
       */
      vehiclesModal: {
        isVisible: false,
        current_kolonne_id: '',
        current_vehicles: [],
        current_kolonne_number: ''
      },
      shouldShowVehiclesModal: false,
      selectedVehicleIds: undefined,
      shouldShowEmployeesModal: false,
      selectedEmployeeIds: undefined,
      selectedKolonneId: undefined,
      selectedKolonneNumber: undefined,
      de: de
    };
  },
  components: {
    vPageLoading: PageLoading,
    vActionButtons: ActionButtons,
    vDatepicker: Datepicker,
    vSelectKolonne: SelectKolonne,
    vVehiclesModal: VehiclesModal,
    AutosizeTextarea,
    KolonneSettingsEmployeesModal,
    KolonneSettingsVehiclesModal,
    KolonneSettingsAssignedVehicle,
    KolonneSettingsCrudVehiclesRankingLegend,
    KolonneSettingsAssignedEmployee,
    KolonneSettingsCrudEmployeesRankingLegend,
  },
  created: function() {
    this.getStartDate();
    this.parseKolonneSettings();
  },
  computed: {
    getPageTitle: function() {
      return this.$t("weeks.kolonne.settings.title");
    },
    getActionButtons: function() {
      let buttons = [];
      buttons.push({
        action: "save"
      });
      buttons.push({
        action: "duplicate"
      });

      return buttons;
    }
  },
  watch: {
    $route: {
      handler: function() {
        this.parseKolonneSettings();
      },
      deep: true
    }
  },
  methods: {
    
    onClickAction: function(action) {
        switch(action) {
            case 'save': this.saveKolonneSettings(); break;
            case 'duplicate': this.triggerDuplicateAll(); break;
        }
    },
    
    parseKolonneSettings: function() {
      this.isLoading = true;
      this.kolonne_content = [];

      Promise.all([
        this.getKolonne(),
        this.getGeocaptureData(),
        this.getKolonneSettings(),
      ]).then(() => {
        this.createKolonneContent()
      }).finally(() => {
        this.isLoading = false;
      });
    },

    createKolonneContent () {
      this.kolonne_content = this.kolonne.map(kolonne => {
        const output = {
          kolonne: kolonne.number,
          kolonne_id: kolonne.id,
          week_startdate: this.startdate,
          active: false,
          vehicles: kolonne.vehicles,
        }

        const kolonneSettingsForKolonne = this.kolonne_settings.find(settings => {
          return kolonne.number == settings.kolonne
        });

        // If we get matches, output that information.
        // If we have no matches, we output just the kolonne number
        // and create empty fields

        if (kolonneSettingsForKolonne) {
          output.crew = kolonneSettingsForKolonne.crew;
          output.id = kolonneSettingsForKolonne.id;
          output.contact = kolonneSettingsForKolonne.contact;
          output.active = kolonneSettingsForKolonne.active;
          output.vehicles = kolonneSettingsForKolonne.vehicles;
          output.comment = kolonneSettingsForKolonne.comment;

          /**
           * @typedef {import('../../asphaltbau-crm').GeocaptureEmployee} GeocaptureEmployee
           * @type {GeocaptureEmployee[]}
           */
          const employees = []
            .concat(kolonneSettingsForKolonne.employees || [])
            .filter(Boolean);
          output.employees = employees;
          output.employees_ids = [].concat(kolonneSettingsForKolonne.employees_ids || []);

          /**
           * @typedef {import('../../asphaltbau-crm').GeocaptureVehicle} GeocaptureVehicle
           * @type {GeocaptureVehicle[]}
           */
          const fleet = []
            .concat(kolonneSettingsForKolonne.fleet || [])
            .filter(Boolean);
          output.fleet = fleet.sort((a,b) => a.id.localeCompare(b.id))
          output.fleet_ids = [].concat(kolonneSettingsForKolonne.fleet_ids || []);
        }

        return output
      });
    },

    openEmployeesModal (kolonneId, kolonneNumber) {
      this.shouldShowEmployeesModal = true;
      this.selectedKolonneId = kolonneId
      this.selectedKolonneNumber = kolonneNumber

      const kolonne = this.kolonne_content.find(kolonne => kolonne.id === kolonneId)
      this.selectedEmployeeIds = kolonne?.employees_ids ?? [];
    },

    onEmployeesSavedFromModal (kolonneSettings) {
      const existingSettingsIndex = this.kolonne_settings.findIndex(settings => settings.id === kolonneSettings.id)

      if (existingSettingsIndex > -1) {
        this.kolonne_settings.splice(existingSettingsIndex, 1, kolonneSettings)
      } else {
        console.error('Failed to find existing kolonne settings after adding employees in modal', { kolonneSettings })
      }

      this.shouldShowEmployeesModal = false;
      this.createKolonneContent()
    },

    onEmployeesRefreshed (employees) {
      this.allEmployees = employees
    },

    /** Vehicle Modal Methods */
    openVehiclesModal (kolonneId, kolonneNumber) {
      this.shouldShowVehiclesModal = true;
      this.selectedKolonneId = kolonneId
      this.selectedKolonneNumber = kolonneNumber

      const kolonne = this.kolonne_content.find(kolonne => kolonne.id === kolonneId)
      this.selectedVehicleIds = kolonne?.fleet_ids ?? [];
    },

    onVehiclesSavedFromModal (kolonneSettings) {
      const existingSettingsIndex = this.kolonne_settings.findIndex(settings => settings.id === kolonneSettings.id)

      if (existingSettingsIndex > -1) {
        this.kolonne_settings.splice(existingSettingsIndex, 1, kolonneSettings)
      } else {
        console.error('Failed to find existing kolonne settings after adding vehicles in modal', { kolonneSettings })
      }

      this.shouldShowVehiclesModal = false;
      this.createKolonneContent()
    },

    onVehiclesRefreshed (vehicles) {
      this.allVehicles = vehicles
    },

    openDeprecatedVehiclesModal: function(kolonneId, vehicles, number){
      this.vehiclesModal.isVisible = true;
      this.vehiclesModal.current_kolonne_id = kolonneId;
      this.vehiclesModal.current_vehicles = vehicles;
      this.vehiclesModal.current_kolonne_number = number;
    },

    refreshVehiclesFromModal: function(value) {
      window.app.$helpers.logger("Refreshing vehicles ...", value);

      this.kolonne_content.forEach(item => {
         if (item.id === value.kolonne_id) {
            item.vehicles = value.vehicles;
         }
      })
    },

    /** Getting Methods */
    getKolonne: function() {
      return this.$api
        .clearParams()
        .get("kolonne")
        .then(response => {
          this.kolonne = response.data.data;
        })
        .catch(error => {
          window.app.$helpers.logger(error);
        });
    },

    getGeocaptureData () {
      return this.$api
        .clearParams()
        .addParam('startdate', this.startdate)
        .get("geocapture/data")
        .then(response => {
          this.allEmployees = response.data.data?.employees ?? [];
          this.allVehicles = response.data.data?.fleet ?? [];
        })
        .catch(error => {
          window.app.$helpers.logger(error);
        });
    },

    getKolonneSettings: function() {
      return this.$api
        .clearParams()
        .addParam("startdate", this.startdate)
        .get("kolonne-settings")
        .then(response => {
          this.kolonne_settings = response.data.data;
        })
        .catch(error => {
          window.app.$helpers.logger(error);
        });
    },

    getStartDate: function() {
      let date = this.$moment().format();
      if (this.$route.params.date) {
        date = this.$moment(this.$route.params.date, "YYYY-MM-DD");
      }
      this.startdate = date.format("YYYY-MM-DD");
      this.week = date.format("w");
    },

    /** Saving Methods */
    shouldSaveSettings: function(item) {
      if (!item) {
        return false;
      }

      if (item.id) {
        return true;
      }

      if (item.active || item.crew || item.contact) {
        return true;
      }

      return false;
    },

    saveKolonneSettings: function() {
      this.isLoading = true;

      let promises = [];

      this.kolonne_content.forEach(item => {
        if (this.shouldSaveSettings(item)) {
          let promise;
          if (item.id) {
            promise = this.$api
              .clearParams()
              .put("kolonne-settings/" + item.id + "/", item);
          } else {
            promise = this.$api.clearParams().post("kolonne-settings", item);
          }

          promises.push(promise);
        }
      });

      Promise.all(promises)
        .then(() => {
          this.isLoading = false;
          this.parseKolonneSettings();
        })
        .catch(error => {
          this.isLoading = false;
          window.app.$helpers.logger(error);
        })
        .finally(() => {
          if (this.duplicateAll) {
            this.duplicateAllSettings();
          }
        });
    },

    /** Duplication Methods */
    duplicateAllSettings: function() {
      const ids = this.kolonne_settings
        .map(item => item.id)
        .toString(); // api expects comma separated ids as string

      let body = {
        ids,
        target_date: this.$moment(this.startdate)
          .add(1, "Week")
          .format("YYYY-MM-DD")
      };

      this.isLoading = true;

      this.$api
        .clearParams()
        .post("kolonne-settings/duplicate", body)
        .then(response => {})
        .catch(error => {
          window.app.$helpers.logger(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.duplicateAll = false;
        });
    },

    triggerDuplicateAll: function() {
      this.duplicateAll = true;
      this.saveKolonneSettings();
    },

    onOpenDuplicateDialog: function(item) {
      this.showDuplicateDialog = true;
      this.duplicateItem = { ...item };
      this.duplicateItem.target_kolonne = item.kolonne;
      this.duplicateItem.next_week_startdate = this.$moment(item.week_startdate)
        .add(1, "Week")
        .format("YYYY-MM-DD");
    },

    onCloseDuplicateDialog: function() {
      this.showDuplicateDialog = false;
      this.duplicateItem.id = 0;
    },

    duplicateKolonneSettings: function() {
      this.isLoading = true;

      let body = {
        target_date: this.duplicateItem.target_date,
        target_kolonne: this.duplicateItem.target_kolonne
      };

      this.$api
        .clearParams()
        .post("kolonne-settings/" + this.duplicateItem.id + "/duplicate", body)
        .then(() => {
          this.onCloseDuplicateDialog();
          this.parseKolonneSettings();
        })
        .catch(error => {
          window.app.$helpers.logger(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>
